/* Factumis App — Factures récurrentes (liste + création + pause).
   Un modèle (client + lignes + cadence) génère un brouillon de facture à chaque
   échéance ; la génération réelle est faite côté serveur par le scheduler. */
import { useState } from 'react';
import { Icon } from '../lib/icons';
import { Button, Field, Modal, StatusPill, EmptyState, eur } from '../components/ui';
import { CompanySearch } from '../components/CompanySearch';
import { LineBuilder, blankLine, linesToPayload, type Line } from '../components/LineBuilder';
import { useRecurring, useCatalog, createRecurring, toggleRecurring, type Cadence, type RecurringInvoice } from '../data/live';
import { errorMessage } from '../lib/api';

const CADENCES: { value: Cadence; label: string }[] = [
  { value: 'monthly', label: 'Mensuelle' },
  { value: 'quarterly', label: 'Trimestrielle' },
  { value: 'yearly', label: 'Annuelle' },
];
const cadenceLabel = (c: Cadence) => CADENCES.find((x) => x.value === c)?.label ?? c;

function frDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
}

function CreateRecurringModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [clientName, setClientName] = useState('');
  const [siren, setSiren] = useState('');
  const [email, setEmail] = useState('');
  const [cadence, setCadence] = useState<Cadence>('monthly');
  const [firstRun, setFirstRun] = useState('');
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  const catalog = useCatalog();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setClientName(''); setSiren(''); setEmail(''); setCadence('monthly'); setFirstRun('');
    setLines([blankLine()]); setError(null);
  }

  const ht = lines.reduce((s, l) => s + l.q * l.pu, 0);
  const tva = lines.reduce((s, l) => s + l.q * l.pu * (l.tva / 100), 0);

  async function submit() {
    const payloadLines = linesToPayload(lines);
    if (!clientName.trim()) { setError('Indiquez un client.'); return; }
    if (payloadLines.length === 0) { setError('Ajoutez au moins une ligne.'); return; }
    if (!firstRun) { setError('Choisissez la date de première génération.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await createRecurring({
        clientName: clientName.trim(),
        clientSiren: siren.trim() || undefined,
        clientEmail: email.trim() || undefined,
        lines: payloadLines,
        cadence,
        firstRun,
      });
      reset();
      onCreated();
    } catch (err) {
      setError(errorMessage(err, 'Création impossible. Réessayez.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  return (
    <Modal open={open} onClose={() => (submitting ? null : onClose())} title="Nouvelle facture récurrente" wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <CompanySearch
          label="Client"
          help="Cherchez par nom ou SIREN, ou saisissez le nom à la main ci-dessous."
          placeholder="Nom du client ou SIREN"
          onPick={(co) => { setClientName(co.name); setSiren(co.siren); }}
        />
        <Field label="Nom du client">
          <input className="ctrl" placeholder="Nom du client" value={clientName} onChange={(ev) => setClientName(ev.target.value)} />
        </Field>
        <Field label="Email du client" opt="recommandé" help="Nécessaire pour l’envoi automatique du brouillon généré.">
          <input className="ctrl" type="email" placeholder="compta@client.fr" value={email} onChange={(ev) => setEmail(ev.target.value)} />
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Cadence">
            <select className="ctrl" value={cadence} onChange={(ev) => setCadence(ev.target.value as Cadence)}>
              {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Première génération">
            <input className="ctrl" type="date" value={firstRun} onChange={(ev) => setFirstRun(ev.target.value)} />
          </Field>
        </div>

        <div style={{ marginTop: 6 }}>
          <div className="kpi__k" style={{ marginBottom: 8 }}>Lignes</div>
          <LineBuilder lines={lines} setLines={setLines} catalog={catalog.rows} />
        </div>

        <div className="totals" style={{ marginTop: 14 }}>
          <div className="row"><span className="muted">Total HT</span><span className="v">{eur(ht)}</span></div>
          <div className="row"><span className="muted">TVA</span><span className="v">{eur(tva)}</span></div>
          <div className="row grand"><span>Total TTC</span><span className="v">{eur(ht + tva)}</span></div>
        </div>

        {error ? (
          <div className="callout-err" style={{ marginTop: 4, marginBottom: 4 }}>
            <Icon name="alert" size={18} />
            <div><div className="t">Action impossible</div><div className="d">{error}</div></div>
          </div>
        ) : null}
      </div>

      <div className="acts">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" icon="check" onClick={() => void submit()} disabled={submitting}>
          {submitting ? 'Création…' : 'Créer la récurrence'}
        </Button>
      </div>
    </Modal>
  );
}

export function RecurringList() {
  const { rows, source, reload } = useRecurring();
  const [createOpen, setCreateOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function toggle(r: RecurringInvoice) {
    setBusyId(r.id);
    try {
      await toggleRecurring(r.id, !r.active);
      reload();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="shead">
        <div className="shead__l">
          <h1>Factures récurrentes</h1>
          <p>Programmez un modèle ; un brouillon de facture est généré automatiquement à chaque échéance.</p>
        </div>
        <div className="shead__r">
          <Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>Nouvelle récurrence</Button>
        </div>
      </div>

      <div className="tbl-wrap">
        {source === 'error' ? (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' }}>
            <span className="muted">Impossible de charger les récurrences.</span>
            <Button variant="ghost" size="sm" onClick={reload}>Réessayer</Button>
          </div>
        ) : source === 'live' && rows.length === 0 ? (
          <EmptyState
            glyph="clock"
            title="Aucune facture récurrente"
            actions={<Button variant="primary" icon="plus" onClick={() => setCreateOpen(true)}>Créer ma première récurrence</Button>}
          >
            Idéal pour les abonnements et forfaits : définissez le client, les lignes et la cadence une seule fois.
          </EmptyState>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Client</th>
                <th>Cadence</th>
                <th>Prochaine génération</th>
                <th className="right">Montant TTC</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const ttc = r.lines.reduce((s, l) => s + l.quantity * l.price * (1 + l.vat / 100), 0);
                return (
                  <tr key={r.id} style={{ cursor: 'default' }}>
                    <td><b>{r.clientName}</b></td>
                    <td>{cadenceLabel(r.cadence)}</td>
                    <td className="muted">{frDate(r.nextRun)}</td>
                    <td className="right amt">{eur(ttc)}</td>
                    <td><StatusPill status={r.active ? 'active' : 'paused'} /></td>
                    <td className="right">
                      <Button variant="ghost" size="sm" onClick={() => void toggle(r)} disabled={busyId === r.id}>
                        {r.active ? 'Mettre en pause' : 'Reprendre'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CreateRecurringModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); reload(); }} />
    </div>
  );
}
